import useSWR from 'swr';
import { useGlobalModules } from 'contexts';
import { parseShopifyGid } from 'lib/shopify/shopifyGid';
import { selectUpsellItems } from 'lib/cart/upsell/selectUpsellItems';
import { getUpsellSourceProductIds } from 'lib/cart/upsell/getUpsellSourceProductIds';
import { useCart } from './useCart';

// Production source = COMPLEMENTARY (client-confirmed 2026-06-17), now that
// complementary products are configured in Shopify.
const RECOMMENDATION_INTENT = 'COMPLEMENTARY';
const DEFAULT_MAX_UPSELL = 4;
const DEFAULT_HIDE_THRESHOLD = 3;

const fetcher = (...urls) => Promise.all(urls.map(u => fetch(u).then(r => r.json())));

export function useCartUpsell(locale) {
  const { cart } = useCart();
  const globalModules = useGlobalModules();
  const upsellSettings = globalModules?.settings?.upsell || {};

  const enabled = upsellSettings.enabled !== false;
  const maxProducts = Number(upsellSettings.maxProducts) || DEFAULT_MAX_UPSELL;
  const hideThreshold = Number(upsellSettings.hideThreshold) || DEFAULT_HIDE_THRESHOLD;

  const lines = cart?.lines?.edges || [];
  const cartProductCount = lines.length;

  const cartProductIds = lines
    .map(({ node }) => parseShopifyGid(node?.merchandise?.product?.id))
    .filter(Boolean)
    .map(String);

  // Pool the complementary products of every product in the cart, newest-first
  // (excluding gift lines), so the slider stays populated regardless of which
  // product was added last (JW 2026-06-26). De-dupe, exclude-in-cart and the
  // max cap happen in selectUpsellItems; the hide rule keeps the cart below
  // hideThreshold, so this is at most a couple of fetches.
  const sourceProductIds = getUpsellSourceProductIds(lines).map(parseShopifyGid).filter(Boolean);
  const shouldFetch = enabled && cartProductCount < hideThreshold && sourceProductIds.length > 0;

  const urls = shouldFetch
    ? sourceProductIds.map(
        id =>
          `/api/shopify/utils/getProductRecommendations?productId=${id}&locale=${locale}&intent=${RECOMMENDATION_INTENT}`
      )
    : [];

  const { data } = useSWR(urls.length ? urls : null, fetcher);

  const items = selectUpsellItems({
    responses: data,
    cartProductIds,
    cartProductCount,
    maxProducts,
    hideThreshold,
  });

  return { items };
}

export default useCartUpsell;
